import React, { useEffect, useState } from 'react';
import { Card, Skeleton } from '@mui/material';
import PersonAddDisabledIcon from '@mui/icons-material/PersonAddDisabled';
import { useSelector } from 'react-redux';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const FutureMetricCard = () => {
  const [idleManPower, setIdleManPower] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});
  const currentDate = useSelector((state) => state.customization.currentDate);
  const formattedDate = currentDate.toISOString().split('T')[0];

  const fetchIdleManPower = async () => {
    try {
      const response = await fetch('https://neoe2e.neophyte.live/fm-api/v2/idle_man_count', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: formattedDate })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }

      const data = await response.json();
      setIdleManPower(data);

      // Initialize all items as collapsed
      const initialExpandedState = {};
      data.forEach((_, index) => {
        initialExpandedState[index] = false;
      });
      setExpandedItems(initialExpandedState);
    } catch (error) {
      console.error('Error fetching idle manpower:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIdleManPower();
  }, [formattedDate]);

  const toggleExpand = (index) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const totalIdleCount = idleManPower.reduce((sum, item) => sum + item.count, 0);

  const formatTime = (timeString) => {
    if (!timeString) return 'Unknown time';
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch (e) {
      return timeString;
    }
  };

  return (
    <div>
      <Card
        className="border relative border-gray-300"
        sx={{
          height: '276px',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px'
        }}
      >
        <div className="flex w-full flex-col gap-1 p-3">
          <div className="flex items-center gap-2">
            <PersonAddDisabledIcon className="text-white rounded-full p-2 text-6xl" style={{ backgroundColor: '#D32F2F' }} />
            <div style={{ width: loading ? '120px' : 'auto' }}>
              {loading ? (
                <>
                  <Skeleton variant="text" width="100%" height={40} />
                  <Skeleton variant="text" width="80%" height={28} />
                </>
              ) : (
                <>
                  <p className="text-lg font-semibold">
                    Idle Man Power
                    <span className="text-lg font-normal text-gray-500 ml-2">
                      Total: <span>{totalIdleCount}</span>
                    </span>
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="bg-slate-100 flex-grow overflow-y-auto h-[184px] p-2 rounded-lg scrollbar">
            {!loading && <div className="text-xs text-gray-500 italic mt-1">More Statistics</div>}
            {loading
              ? Array(2)
                  .fill(0)
                  .map((_, index) => (
                    <div key={index} className="bg-white rounded p-3 mb-2">
                      <Skeleton variant="text" width="60%" height={30} />
                      <Skeleton variant="text" width="40%" height={20} />
                    </div>
                  ))
              : idleManPower.map((item, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 mb-2">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-semibold ">{item.device}</span>
                      <div className="flex items-center cursor-pointer" onClick={() => toggleExpand(index)}>
                        <span className="text-sm font-medium mr-1">{item.count}</span>
                        {expandedItems[index] ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                      </div>
                    </div>

                    {expandedItems[index] && (
                      <div className="border-t pt-2">
                        {item.idleMen?.map((person, personIndex) => (
                          <div key={personIndex} className="mb-2 ">
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="font-medium text-lg">Person {personIndex + 1}</div>
                                <div className="font-medium text-gray-500 mt-1 flex items-center">
                                  <AccessTimeIcon style={{ fontSize: '16px', marginRight: '8px' }} />
                                  <span>Since: {formatTime(person.start_time)}</span>
                                </div>
                              </div>
                              <div className="flex items-center text-red-500 font-semibold text-lg mt-6">
                                <AccessAlarmIcon style={{ fontSize: '16px', marginRight: '4px' }} />
                                <span>{person.idle_time} min</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default FutureMetricCard;
